import { useEffect, useState } from "react";
import axios from "axios";

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [processingId, setProcessingId] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackNotificationId, setFeedbackNotificationId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const userId = localStorage.getItem("userId"); // or from auth context if you have it

  const fetchNotifications = async () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');
      const res = await axios.get(apiBase + `/api/notifications/${userId}`);
      setNotifications(res.data);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  };

  useEffect(() => {
    if (userId) fetchNotifications();
  }, [userId]);

  // reset to first page when notifications change
  useEffect(() => {
    setCurrentPage(1);
  }, [notifications.length]);

  const respond = async (notificationId, action) => {
    if (processingId) return;
    setProcessingId(notificationId);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // For Termination accept, allow sending customer feedback (handled by modal)
      const res = await axios.patch(apiBase + `/api/notifications/${notificationId}/respond`, { action }, { headers });
      if (res.data && res.data.success) {
        // If the server created a Project as part of accepting the invoice, it will return `createdProjectId`.
        // We don't navigate the customer, but we surface a message. Admin will see the project in their dashboard.
        const projectId = res.data.createdProjectId;
        if (projectId) {
          console.log('Project created from invoice accept:', projectId);
          // Optionally you could notify the user or open a link; for now we simply inform and refresh notifications.
          alert(res.data.message || 'Response recorded. Project created.');
        } else {
          alert(res.data.message || 'Response recorded');
        }
        // If the updated notification contains a requirementId in meta, write a localStorage key
        try {
          const updated = res.data.notification;
          const reqId = updated && updated.meta && updated.meta.requirementId;
          if (reqId) {
            // write timestamp so other tabs/components can react
            localStorage.setItem(`requirement_update_${reqId}`, String(Date.now()));
            // also set a global flag so sidebar Add Client unlocks
            try { localStorage.setItem('hasAcceptedRequirement', 'true'); } catch (e) { }
          }
        } catch (e) {
          // ignore
        }
        await fetchNotifications();
      } else {
        alert('Failed to record response');
      }
    } catch (err) {
      console.error('Failed to respond to notification', err);
      alert('Failed to respond to notification');
    } finally {
      setProcessingId('');
    }
  };

  const openFeedbackModal = (notificationId) => {
    setFeedbackNotificationId(notificationId);
    setFeedbackText('');
    setShowFeedbackModal(true);
  };

  const submitFeedback = async () => {
    if (!feedbackNotificationId) return;
    setProcessingId(feedbackNotificationId);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Send feedback along with accept action
      const res = await axios.patch(apiBase + `/api/notifications/${feedbackNotificationId}/respond`, { action: 'accepted', feedback: feedbackText }, { headers });
      if (res.data && res.data.success) {
        alert('Thank you for your feedback');
        setShowFeedbackModal(false);
        await fetchNotifications();
      } else {
        alert('Failed to send feedback');
      }
    } catch (err) {
      console.error('Failed to send feedback', err);
      alert('Failed to send feedback');
    } finally {
      setProcessingId('');
    }
  };

  return (
    <>
      <h2 className="text-2xl font-semibold mb-4 text-white">Notifications</h2>

      <div className="p-6 bg-white shadow rounded-md">
        {(!notifications || notifications.length === 0) ? (
          <p className="text-gray-500">You have no notifications.</p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-gray-600">Showing {(notifications.length === 0) ? 0 : ((currentPage-1)*pageSize+1)} - {Math.min(currentPage*pageSize, notifications.length)} of {notifications.length}</div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Per page:</label>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} className="border rounded px-2 py-1">
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>
            <ul className="divide-y divide-gray-200">
              {notifications.slice((currentPage-1)*pageSize, currentPage*pageSize).map((note) => (
                <li key={note._id} className="py-3">
                <p className="text-gray-800">{note.message}</p>
                <p className="text-xs text-gray-400">{new Date(note.date || note.createdAt).toLocaleString()}</p>

                {/* If this notification is actionable (Quotation, Invoice or Termination) and pending, show action buttons */}
                {['Quotation', 'Invoice', 'Termination'].includes(note.actionType) && note.status === 'pending' && (
                  <div className="mt-2 flex gap-2">
                    <button
                      className="px-3 py-1 bg-green-600 text-white rounded"
                      onClick={() => {
                        // If Termination, open feedback modal before sending accept
                        if (note.actionType === 'Termination') {
                          openFeedbackModal(note._id);
                        } else {
                          respond(note._id, 'accepted');
                        }
                      }}
                      disabled={processingId === note._id}
                    >{processingId === note._id ? 'Processing...' : (note.actionType === 'Invoice' ? 'Pay Now' : 'Accept')}</button>
                    <button
                      className="px-3 py-1 bg-red-600 text-white rounded"
                      onClick={() => respond(note._id, 'declined')}
                      disabled={processingId === note._id}
                    >{processingId === note._id ? 'Processing...' : 'Decline'}</button>
                    {note.meta && (note.meta.quotationUrl || note.meta.invoiceUrl) && (
                      <a href={note.meta.quotationUrl || note.meta.invoiceUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-gray-800 text-white rounded">View</a>
                    )}
                  </div>
                )}

                </li>
              ))}
            </ul>

            {/* Pagination controls */}
            <div className="flex items-center justify-between mt-4">
              <div>
                <button className="px-3 py-1 border rounded mr-2" onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage===1}>Prev</button>
                <button className="px-3 py-1 border rounded" onClick={() => setCurrentPage(p => Math.min(Math.ceil(notifications.length/pageSize), p+1))} disabled={currentPage>=Math.ceil(notifications.length/pageSize)}>Next</button>
              </div>
              <div className="text-sm text-gray-600">
                Page {currentPage} of {Math.max(1, Math.ceil(notifications.length/pageSize))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Feedback modal for termination accept */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded p-6 w-full max-w-xl">
            <h3 className="text-lg font-semibold mb-2">Termination Feedback</h3>
            <p className="text-sm text-gray-600 mb-4">Please provide feedback or reason for accepting termination. This will be sent to your admin.</p>
            <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={6} className="w-full border p-2 rounded mb-4" />
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2 bg-gray-300 rounded" onClick={() => setShowFeedbackModal(false)}>Cancel</button>
              <button className="px-4 py-2 bg-green-600 text-white rounded" onClick={submitFeedback} disabled={processingId === feedbackNotificationId}>{processingId === feedbackNotificationId ? 'Sending...' : 'Send Feedback'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notification;
